import { useCallback, useEffect, useMemo, useState } from 'react'
import { Gift, Loader2, Package2, Search, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import { getAssignedCustomer } from '@/components/pos/posCustomer'
import { fetchPosPackageCatalog } from '@/lib/api/packages'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  filterPosPackages,
  packageRequiresRecipient,
  posPackagePriceLabel,
  posPackageTypeLabel,
} from '@/lib/pos/posPackage'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {() => void} props.onClose
 * @param {(payload: { package_id: number, recipient_name?: string }) => Promise<boolean>} props.onSell
 * @param {boolean} props.submitting
 * @param {string} [props.error]
 */
export function PosPackageWizardModal({
  open,
  sale,
  onClose,
  onSell,
  submitting,
  error = '',
}) {
  const { activeBranch } = useBranch()
  const [query, setQuery] = useState('')
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(false)
  const [localError, setLocalError] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [recipientName, setRecipientName] = useState('')

  const customer = getAssignedCustomer(sale)
  const selectedPackage = packages.find((pkg) => pkg.id === selectedId) ?? null

  const visiblePackages = useMemo(
    () => filterPosPackages(packages, query),
    [packages, query],
  )

  const loadPackages = useCallback(async () => {
    setLoading(true)
    setLocalError('')

    try {
      const items = await fetchPosPackageCatalog()
      setPackages(items)
    } catch (err) {
      setLocalError(apiErrorMessage(err, 'Unable to load packages.'))
      setPackages([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setSelectedId(null)
      setRecipientName('')
      setLocalError('')
      return
    }

    loadPackages()
  }, [loadPackages, open])

  const handleSell = useCallback(async () => {
    if (!selectedPackage) return

    const payload = { package_id: selectedPackage.id }

    if (packageRequiresRecipient(selectedPackage)) {
      const trimmed = recipientName.trim()
      if (!trimmed) {
        setLocalError('Recipient name is required for gift vouchers.')
        return
      }
      payload.recipient_name = trimmed
    }

    const sold = await onSell(payload)
    if (sold) {
      onClose()
    }
  }, [onClose, onSell, recipientName, selectedPackage])

  if (!open) return null

  const displayError = error || localError
  const needsCustomer = !customer

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={submitting ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-package-wizard-title"
        className="card-surface flex max-h-[min(82vh,680px)] w-full max-w-2xl flex-col overflow-hidden shadow-luxury-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-border/50 px-4 py-3">
          <div>
            <p id="pos-package-wizard-title" className="flex items-center gap-2 text-sm font-semibold">
              <Package2 className="h-4 w-4 text-primary" />
              Sell package
            </p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Combo, wallet, membership, voucher, or session pack · F6 shortcut
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {needsCustomer ? (
          <div className="border-b border-amber-200/60 bg-amber-50 px-4 py-3 text-[11px] text-amber-900">
            Assign a customer before selling a package (F2 — customer search).
          </div>
        ) : (
          <div className="border-b border-border/40 bg-muted/20 px-4 py-2 text-[11px] text-muted-foreground">
            Selling to <span className="font-semibold text-foreground">{customer.name}</span>
          </div>
        )}

        <div className="grid min-h-0 flex-1 md:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <div className="flex min-h-0 flex-col border-b border-border/40 md:border-b-0 md:border-r">
            <div className="border-b border-border/40 px-4 py-3">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search packages…"
                  disabled={loading || submitting}
                  className="w-full rounded-xl border border-input bg-background py-2 pl-9 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading packages…
                </div>
              ) : visiblePackages.length === 0 ? (
                <div className="px-2 py-8 text-center text-[11px] text-muted-foreground">
                  <p>No sellable packages for {activeBranch?.name ?? 'this branch'}.</p>
                  <p className="mt-2">
                    In Masters → Packages, enable <strong>Active</strong> and{' '}
                    <strong>Sellable on POS</strong>, and use the same branch in the header.
                  </p>
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {visiblePackages.map((pkg) => {
                    const selected = pkg.id === selectedId

                    return (
                      <li key={pkg.id}>
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => {
                            setSelectedId(pkg.id)
                            setLocalError('')
                          }}
                          className={cn(
                            'w-full rounded-xl border px-3 py-2.5 text-left transition-colors',
                            selected
                              ? 'border-primary/40 bg-primary/5 ring-1 ring-primary/20'
                              : 'border-border/50 bg-card hover:border-primary/20 hover:bg-muted/30',
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold">{pkg.name}</p>
                              <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                                {posPackageTypeLabel(pkg.type)}
                              </p>
                              <p className="mt-1 text-[11px] text-muted-foreground">{pkg.summary}</p>
                            </div>
                            <span className="shrink-0 text-sm font-bold text-foreground">
                              {posPackagePriceLabel(pkg)}
                            </span>
                          </div>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="flex min-h-0 flex-col p-4">
            {selectedPackage ? (
              <>
                <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Selected package
                  </p>
                  <p className="mt-1 text-base font-semibold">{selectedPackage.name}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">{selectedPackage.summary}</p>
                  <p className="mt-3 text-2xl font-bold text-primary">
                    {posPackagePriceLabel(selectedPackage)}
                  </p>
                  {selectedPackage.validity_days ? (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Valid for {selectedPackage.validity_days} days after purchase
                    </p>
                  ) : null}
                </div>

                {packageRequiresRecipient(selectedPackage) ? (
                  <label className="mt-4 block">
                    <span className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      <Gift className="h-3 w-3" aria-hidden />
                      Recipient name
                    </span>
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(event) => setRecipientName(event.target.value)}
                      disabled={submitting}
                      placeholder="Who receives this voucher?"
                      className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </label>
                ) : null}
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-border/60 bg-muted/10 px-4 text-center text-[11px] text-muted-foreground">
                Select a package from the list to review price and add it to the ticket.
              </div>
            )}

            {displayError ? (
              <p className="mt-3 text-[11px] text-destructive">{displayError}</p>
            ) : null}

            <div className="mt-auto flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={submitting}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="flex-1"
                disabled={
                  submitting
                  || needsCustomer
                  || !selectedPackage
                  || (packageRequiresRecipient(selectedPackage) && !recipientName.trim())
                }
                onClick={handleSell}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding…
                  </>
                ) : (
                  'Add to ticket'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
